// Single source of truth for schema validation + ownership.
// Pure functions only — no AWS SDK, no native, no nanoid. Imported by the
// Lambda (bundled via fromAsset) AND by the RN app (via src/backend/rules.ts).

export interface RuleValidation {
  type: 'required' | 'min' | 'max' | 'minLength' | 'maxLength' | 'pattern' | 'custom';
  value?: unknown;
  message?: string;
}

export interface RuleField {
  name: string;
  type: string;
  validation?: RuleValidation[];
}

export interface RuleSchema {
  name: string;
  fields: RuleField[];
  ownership?: 'public' | 'user' | 'role' | 'ownerWrite';
  ownerField?: string;
}

export interface RuleUser {
  userId: string;
  /** Present on a real authenticated caller; used to tell an owner WHO submitted a row. */
  email?: string;
}

/**
 * The field an owner reads to see who sent something.
 *
 * Server-written only. A visitor signed in with their Saddle account is somebody the owner
 * should be able to identify — that is the point of asking them to sign in — but the client
 * must never be able to claim it, or a form post could name anybody.
 */
export const SUBMITTED_BY = 'submittedBy';

export type AccessAction = 'read' | 'create' | 'update' | 'delete';

/**
 * Keys a request body may never carry, whatever the collection says.
 *
 * These are the row's ADDRESS and its lifecycle, not its content: the table keys, the index
 * keys, the tombstone and the expiry. A body that sets them is choosing where it is stored
 * and how long it lives, and the write path used to let it — a create spread the body over
 * the key fields, so `{"PK":"APP#someone-else","SK":"MEMBER#me","role":"owner"}` wrote a
 * membership row granting the sender ownership of another person's app.
 *
 * Deliberately NOT here: `role`, `credits` and similar. They are dangerous only in a row the
 * body can no longer address, and a volunteer rota with a `role` column or a loyalty app with
 * `credits` is an ordinary app that must keep working. The address is the invariant; the
 * content is the app author's business.
 */
export const RESERVED_KEYS: readonly string[] = ['PK', 'SK', 'GSI1PK', 'GSI1SK', 'ttl', 'deleted'];

/**
 * `allowUnknown` relaxes which COLUMNS a collection may have. It was never meant to relax
 * this, so the check sits outside it: an owner-managed or per-user collection still decides
 * its own shape, it just cannot decide where its rows go.
 */
export function reservedKeyErrors(data: Record<string, unknown>): string[] {
  return Object.keys(data)
    .filter((key) => RESERVED_KEYS.includes(key))
    .map((key) => `Reserved field: ${key}`);
}

/** Per-app role of the requesting user (resolved by the handler from enrollments).
 *  'admin' = an end-user the creator promotes to manage users/data (sees all rows)
 *  but not edit the app definition. 'user' = default end-user (own rows only);
 *  'member' is a back-compat alias of 'user'. 'blocked' = access revoked (deny all;
 *  sticks across re-use — auto-enroll won't resurrect it). */
export type AppRole = 'owner' | 'editor' | 'admin' | 'user' | 'member' | 'blocked';

export interface AccessContext {
  /** The caller's role for THIS app, resolved server-side. Absent → not a member. */
  appRole?: AppRole;
}

/**
 * `allowUnknown` exists for collections whose shape is the OWNER's to decide.
 *
 * Refusing an undeclared key is the right default: a public form's fields are fixed when
 * the app is built, and anything else arriving is either a mistake or an attempt. But an
 * owner-managed collection — a rota, a timetable — is content, and its columns change when
 * the owner decides they should. Declaring a fixed field list there would mean the server
 * rejecting the very column the owner just added.
 */
export function validateRequestBody(
  data: Record<string, unknown>,
  fields: RuleField[],
  options: { allowUnknown?: boolean } = {},
): string[] {
  const errors: string[] = reservedKeyErrors(data);
  const fieldMap = new Map(fields.map((f) => [f.name, f]));

  if (!options.allowUnknown) {
    for (const key of Object.keys(data)) {
      if (key === 'id') continue;
      if (!fieldMap.has(key)) errors.push(`Unknown field: ${key}`);
    }
  }

  for (const field of fields) {
    if (!field.validation) continue;
    for (const rule of field.validation) {
      const err = validateField(field.name, data[field.name], rule);
      if (err) errors.push(err);
    }
  }
  return errors;
}

/**
 * A validation rule is not worth an outage.
 *
 * `validateField` built a regular expression out of the SCHEMA and ran it against the REQUEST
 * BODY. The pattern is the app author's, the value is the visitor's, and neither was bounded.
 * A pattern like `^(a+)+$` takes time exponential in the length of a FAILING input: measured
 * here, thirty-one characters in a form field held a single Lambda for between forty seconds
 * and two minutes, per request.
 *
 * Which is an outage rather than a slow page, because this account's Lambda concurrency limit
 * is 10 rather than the default 1000. Ten such posts occupy every execution slot the account
 * has, and everything else — every app, every published page, every sign-in — is throttled
 * behind them. A throttled invocation never runs, so it never throws and never reaches
 * `Errors`; the metric most people watch would have shown a healthy service throughout.
 *
 * Three different problems, so three bounds. The dangerous PATTERN is never run. The
 * dangerous INPUT fails the rule rather than being tested. And a pattern that is not a
 * regular expression at all no longer throws out of an ordinary form post.
 */

/** Long enough for any pattern a generator writes, short enough to stay cheap to analyse. */
const MAX_PATTERN_LENGTH = 200;

/**
 * Past this, a pattern-constrained value is not a near miss, it is not the field's content.
 * Checking it buys nothing and is precisely where a slow pattern gets expensive.
 */
const MAX_TESTED_LENGTH = 1024;

/** `\d`, a character class and the like, so their contents are not read as structure. */
function withoutLiterals(body: string): string {
  return body.replace(/\\./g, '').replace(/\[[^\]]*\]/g, '');
}

/**
 * Can what follows a group make that group repeat more than once?
 *
 * `?` cannot, which matters more than it looks: `(\+\d{1,3}[- ]?)?\d{10}` is an ordinary phone
 * pattern whose group body is full of quantifiers, and a check that flagged every quantified
 * group would refuse it. Only repetition turns an ambiguous body into backtracking.
 */
function repeatsGroup(after: string): boolean {
  if (after.startsWith('*') || after.startsWith('+')) return true;
  const m = /^\{(\d*)(?:,(\d*))?\}/.exec(after);
  if (!m) return false;
  if (m[2] === undefined) return Number(m[1]) > 1;      // {3} — a fixed count above one
  return m[2] === '' || Number(m[2]) > 1;               // {2,} or {2,5}
}

/**
 * Why a pattern will not be run, or null if it is fine.
 *
 * The shape that backtracks catastrophically is a group that can repeat whose BODY can also
 * match the same text more than one way — another quantifier, or an alternation. Conservative
 * by design: it is a static property of the pattern text, so the decision is deterministic and
 * says nothing about the request, the environment or the time of day.
 */
export function unusablePattern(pattern: string): string | null {
  if (pattern.length > MAX_PATTERN_LENGTH) return 'pattern is too long to be checked';

  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '\\') { i++; continue; }
    if (pattern[i] !== '(') continue;

    let depth = 1;
    let j = i + 1;
    for (; j < pattern.length && depth > 0; j++) {
      if (pattern[j] === '\\') { j++; continue; }
      if (pattern[j] === '(') depth++;
      else if (pattern[j] === ')') depth--;
    }
    // An unbalanced group is not a regular expression; refuse it here rather than let
    // `new RegExp` throw out of a form post.
    if (depth !== 0) return 'pattern has an unclosed group';

    if (!repeatsGroup(pattern.slice(j))) continue;
    if (/[*+|]|\{\d*,/.test(withoutLiterals(pattern.slice(i + 1, j - 1)))) {
      return 'a repeating group whose body also repeats can backtrack catastrophically';
    }
  }
  return null;
}

export function validateField(name: string, value: unknown, rule: RuleValidation): string | null {
  const msg = rule.message ?? `Validation failed for ${name}`;
  switch (rule.type) {
    case 'required':
      if (value === undefined || value === null || value === '') return msg;
      break;
    case 'min':
      if (typeof value === 'number' && value < (rule.value as number)) return msg;
      break;
    case 'max':
      if (typeof value === 'number' && value > (rule.value as number)) return msg;
      break;
    case 'minLength':
      if (typeof value === 'string' && value.length < (rule.value as number)) return msg;
      break;
    case 'maxLength':
      if (typeof value === 'string' && value.length > (rule.value as number)) return msg;
      break;
    case 'pattern': {
      if (typeof value !== 'string') break;
      const pattern = String(rule.value ?? '');

      /*
       * Skipped and RECORDED, not skipped quietly.
       *
       * Refusing every submission to an app whose generator wrote an awkward pattern is the
       * larger harm — a pattern rule is a tidiness constraint, not an access control — so the
       * rule is dropped rather than failed.
       *
       * This is a fallback, and one shipped from this branch a day earlier was the opposite
       * of defensible, so the difference is worth stating rather than leaving to judgement.
       * It is NOT that this one is logged. It is that its TRIGGER IS A STATIC PROPERTY OF THE
       * INPUT TEXT: "this pattern has a repeating group whose body also repeats" is decidable
       * from the pattern alone, deterministically, the same answer in every environment, and
       * it cannot quietly mean anything else. The upload handler's fallback triggered on a
       * configuration parameter being unset — indistinguishable from a half-deployed stack —
       * so it fired on a real misconfiguration and turned it into a silent wrong answer.
       *
       * A trigger that can only mean one thing may be handled; a trigger that could mean
       * something has gone wrong must refuse. The log is the second condition rather than the
       * first: a fallback is a silencer only if nobody can hear it, which is why the line
       * below is asserted by a test rather than merely present — see patternRedos.test.ts,
       * 'records the refusal rather than swallowing it'.
       */
      const unusable = unusablePattern(pattern);
      if (unusable) {
        console.error('[rules] pattern not applied', { field: name, reason: unusable, pattern: pattern.slice(0, 120) });
        break;
      }

      if (value.length > MAX_TESTED_LENGTH) return msg;

      try {
        if (!new RegExp(pattern).test(value)) return msg;
      } catch (e) {
        // A schema that cannot compile is a bug somebody should see, not a 500 for a visitor.
        console.error('[rules] pattern did not compile', { field: name, pattern: pattern.slice(0, 120), error: (e as Error).message });
      }
      break;
    }
  }
  return null;
}

/**
 * What a collection means when it does not say.
 *
 * This used to be `?? 'public'`, and an undeclared collection was therefore world-readable
 * by anyone, signed in or not. A production census found that is not a corner: 38 of 96 live
 * schema rows carry no ownership value, and among them are personal todos, notes and workout
 * logs on apps deliberately kept out of the public catalogue. `crud.ts` does not consult the
 * catalogue, so `GET /apps/<id>/todos` resolved the schema, took the default, and handed a
 * private list to an anonymous caller. One of them is a TEMPLATE, so every app generated from
 * it inherited the same leak.
 *
 * The convenience default had become an access rule nobody chose — the third time in one week
 * an absent field resolved to the permissive answer (`webBookable` absent meant readable,
 * `listed` absent meant listed). So absence now resolves to the narrowest useful meaning:
 * the rows belong to whoever wrote them. A collection that wants to be seen by everyone says
 * so, and the 96 rows that do say so are untouched.
 *
 * An unrecognised value lands here too. It reaches this code from DynamoDB rather than from
 * the type, so `ownership: 'Public'` is reachable in a way the compiler cannot see, and the
 * old `default:` arm answered it with `allowed: true`.
 */
const KNOWN_OWNERSHIP = ['public', 'user', 'role', 'ownerWrite'] as const;

export function effectiveOwnership(schema: RuleSchema): NonNullable<RuleSchema['ownership']> {
  const declared = schema.ownership;
  return declared && (KNOWN_OWNERSHIP as readonly string[]).includes(declared) ? declared : 'user';
}

export function checkAccess(
  schema: RuleSchema,
  action: AccessAction,
  user: RuleUser | null,
  record?: Record<string, unknown>,
  ctx?: AccessContext,
): { allowed: boolean; reason?: string } {
  const ownership = effectiveOwnership(schema);

  switch (ownership) {
    /**
     * Everyone reads. Writing is narrower than it looks, and used to be much wider.
     *
     * Changing an existing row fell through to `allowed: true` whenever the schema declared
     * no `ownerField` — so any signed-in person could edit or delete anything in a public
     * collection of any published app. Being signed in to Saddle is not a relationship with
     * somebody else's restaurant menu, and first use of an app auto-enrols the caller, so the
     * 'user' role could not be what authorised it either.
     *
     * It is not a corner case. The generation prompt tells the model to use `public` for
     * precisely the content this matters most for — a menu, a business profile, a services
     * list, a landing page — and to protect it by hiding the EDIT SCREENS behind
     * `visible: "$.user.role == 'owner'"`. That hides the button; it never stopped the
     * request. This makes the server enforce what the prompt already intends.
     *
     * Creating is untouched: a public form taking submissions from anyone signed in is the
     * whole point of one. Only changing what is already there is narrowed.
     */
    case 'public': {
      if (action === 'read') return { allowed: true };
      if (!user) return { allowed: false, reason: 'Authentication required' };
      if (action === 'create') return { allowed: true };

      // A row that records who wrote it belongs to them, and they may change it.
      if (record && schema.ownerField) {
        if (record[schema.ownerField] === user.userId) return { allowed: true };
        // ...and the app's managers may still moderate what others wrote.
        const moderator = ctx?.appRole;
        if (moderator === 'owner' || moderator === 'editor' || moderator === 'admin') return { allowed: true };
        return { allowed: false, reason: 'Not the owner of this record' };
      }

      /*
       * Nothing records an owner, so nobody owns it — which makes it the app's own content,
       * and the app's own content is its managers' to change.
       *
       * The trade this makes: on a public collection with no `ownerField`, an end user can no
       * longer edit a row they created. They could not be shown to have created it either,
       * which is the same fact from the other side. A collection that wants per-person editing
       * needs to record an owner, and the branch above then does the right thing.
       */
      const role = ctx?.appRole;
      if (role === 'owner' || role === 'editor' || role === 'admin') return { allowed: true };
      return { allowed: false, reason: 'Only the people who manage this app can change this' };
    }

    /**
     * Owner-managed content that visitors read: a rota, a timetable, a class list.
     *
     * Distinct from 'public' because 'public' lets ANY signed-in person write, which is
     * right for a form and wrong for a schedule — and distinct from 'role' because that
     * requires a reader to be signed in, which a published page's visitors are not.
     * Reading is open to everyone, including anonymously; writing is the owner's alone.
     */
    case 'ownerWrite': {
      if (action === 'read') return { allowed: true };
      if (!user) return { allowed: false, reason: 'Authentication required' };
      const role = ctx?.appRole;
      if (role === 'owner' || role === 'editor' || role === 'admin') return { allowed: true };
      return { allowed: false, reason: 'Only the owner of this app can change this' };
    }

    /**
     * Per-person rows — and the default, so this arm now answers for every collection that
     * declares nothing at all.
     *
     * Creating stays open to any signed-in caller: a write-only form whose whole purpose is
     * accepting a submission from somebody with no relationship to the app is 32 of the live
     * collections, and narrowing reads must not close them. Anonymous submissions to a
     * `webBookable` form do not pass through here at all — `handleCreate` admits those before
     * asking.
     */
    case 'user': {
      if (!user) return { allowed: false, reason: 'Authentication required' };
      if (action === 'create') return { allowed: true };

      /*
       * Nothing records an owner, so no row here can be shown to be the caller's.
       *
       * It used to fall through to `allowed: true`, which meant every row was visible to
       * every signed-in caller — and since first use of an app auto-enrols whoever opened it,
       * the 'user' role is not a relationship that can justify that. With no owner recorded,
       * the rows are the app's own content, so the people who manage the app may reach them
       * and nobody else may. One live collection declares `user` with no `ownerField` and was
       * open this way despite declaring itself; the undeclared collections arrive here too.
       */
      if (!schema.ownerField) {
        const role = ctx?.appRole;
        if (role === 'owner' || role === 'editor' || role === 'admin') return { allowed: true };
        return { allowed: false, reason: 'Only the people who manage this app can see this' };
      }

      // A bare list read has no record, so it is allowed here and `handleList` filters it
      // down to the caller's own rows. Deliberately no manager bypass: `scopeToOwn` gives
      // an owner their own rows and not everyone else's, and this must agree with it.
      if (record && record[schema.ownerField] !== user.userId) {
        return { allowed: false, reason: 'Access denied' };
      }
      return { allowed: true };
    }

    case 'role': {
      // Role-scoped data: gated by the caller's per-app role (owner/editor/member).
      // Deny-by-default — no authenticated user or no app role → no access. (This
      // replaces the old stub that allowed any authenticated user.)
      if (!user) return { allowed: false, reason: 'Authentication required' };
      const appRole = ctx?.appRole;
      if (!appRole) return { allowed: false, reason: 'Not a member of this app' };
      if (appRole === 'blocked') return { allowed: false, reason: 'Access to this app has been revoked' };
      // Create their own rows (owner-stamped server-side).
      if (action === 'create') return { allowed: true };
      // Owner/editor/admin may read+modify ANY record.
      if (appRole === 'owner' || appRole === 'editor' || appRole === 'admin') return { allowed: true };
      // user/member may read+modify only their OWN records. (A bare list read has
      // no record here → allowed, then the handler filters the list to own rows.)
      if (record && schema.ownerField && record[schema.ownerField] !== user.userId) {
        return { allowed: false, reason: 'You can only access your own records' };
      }
      return { allowed: true };
    }

    default:
      // Unreachable: effectiveOwnership only ever returns one of the four above. Kept as a
      // refusal rather than the `allowed: true` that stood here, so that if a fifth value is
      // ever added to the union and forgotten here, it fails shut.
      return { allowed: false, reason: 'Access denied' };
  }
}

export function addOwnership(
  schema: RuleSchema,
  data: Record<string, unknown>,
  user: RuleUser | null,
): Record<string, unknown> {
  // Whoever the client says sent this is discarded before anything else. The only source
  // for it is the verified token.
  const clean = { ...data };
  delete clean[SUBMITTED_BY];

  /*
   * A signed-in visitor is somebody the owner can see.
   *
   * A submission used to be anonymous whether or not the person was signed in, so an owner
   * looking at their list could not tell who any of it came from beyond what the form
   * happened to ask. Now that one Saddle account works across every app, signing in is the
   * normal case — and the reason to ask somebody to sign in at all is that it tells the
   * owner who they are.
   */
  const withSender = user?.email ? { ...clean, [SUBMITTED_BY]: user.email } : clean;

  // Always stamp the owner to the authenticated user on create — never trust a
  // client-supplied owner field (prevents spoofing another user's ownership).
  if (user && schema.ownerField) {
    return { ...withSender, [schema.ownerField]: user.userId };
  }
  return withSender;
}
